<template>
  <div>
    <v-btn
      :class="bindClass(schema)"
      :color="primaryWhite"
      :disabled="loading || fieldProps.readonly"
      :loading="loading"
      v-bind="fieldProps"
      @click="runClickLogic"
    >
      {{ label }}
    </v-btn>

    <v-dialog
      v-model="popup.show"
      max-width="650"
      role="dialog"
    >
      <template v-slot:default="{ isActive }">
        <v-card :title="popup.title">
          <v-card-text>
            <vue-schema-forms
              :key="popupReferenceReload"
              ref="popupReference"
              v-model="popup.model"
              :options="popup.options"
              :schema="popup.schema"
            />
          </v-card-text>
          <v-card-actions class="mx-4">
            <v-spacer></v-spacer>

            <v-btn
              :text="t('close')"
              v-bind="{ ...fieldProps, color: '', variant: 'elevated' }"
              @click="isActive.value = false"
            ></v-btn>

            <v-btn
              v-bind="{ ...fieldProps, color: 'primary', variant: 'elevated' }"
              @click="saveDialogForm(isActive)"
            >
              {{ popup.acceptText }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>

  <v-snackbar
    v-model="snackbar.modelValue"
    :timeout="snackbar.timeout"
    :color="snackbar.color"
    :variant="snackbar.variant"
  >
    {{ snackbar.text }}
  </v-snackbar>
  </div>
</template>

<script lang="ts" setup>
import { useEventBus } from '@vueuse/core';
import axios from 'axios';
import get from 'lodash/get';
import { useTheme } from 'vuetify';

import { Ref, computed, onMounted, reactive, ref } from 'vue';

import { useClass, useLabel, useLocale, useProps, useResolveVariables } from '@/core/composables';
import { useEventHandler } from '@/core/composables/useEventHandler';
import { variableRegexp } from '@/core/engine/utils';
import { toast } from '@/main';
import { EngineButtonField } from '@/types/engine/EngineButtonField';
import { NodeUpdateEvent } from '@/types/engine/NodeUpdateEvent';
import { Schema } from '@/types/schema/Schema';

import VueSchemaForms from '../engine/VueSchemaForms.vue';

const vueSchemaFormEventBus = useEventBus<string>('form-model');

const popupReferenceReload = ref(0);
vueSchemaFormEventBus.on(async (event, payload: NodeUpdateEvent | string) => {
  if (payload == 'action-callback' && schema.mode == 'form-and-action') {
    const newModel = get(model, schema.config.modelReference, {});
    if (schema.config.modelReference) {
      popup.model = newModel;
      popupReferenceReload.value++;
    }
  }

  if (payload == 'action-callback') {
    loading.value = false;
  }
});

const actionHandlerEventBus = useEventBus<string>('form-action');
const loading = ref(false);
const { schema, model } = defineProps<{
  schema: EngineButtonField;
  model: object;
}>();

const { bindClass } = useClass();
const { bindProps, fieldProps } = useProps();
const { label, bindLabel } = useLabel(schema);
const { resolve } = useResolveVariables();
const { onClick } = useEventHandler();

const { t } = useLocale();
const theme = useTheme();
const primaryWhite = computed(() => (theme.current.value.dark ? 'white' : 'primary'));

const shouldWaitForSaveState =
  schema.config && schema.config.waitForSaveState ? schema.config.waitForSaveState : false;
const caller = ref<Function>(() => {});
const formCurrentSaveState = ref(true);
if (shouldWaitForSaveState) {
  const formExternalStateEventBus = useEventBus<string>('form-state');
  formExternalStateEventBus.on((event, payload) => {
    formCurrentSaveState.value = payload;
    if (payload == true) {
      caller.value();
      caller.value = () => {};
    }
  });
}

const popupReference = ref();
const popup = reactive<{
  errorMessages: Ref<any[]>;
  show: boolean;
  title: string;
  model: object;
  schema: Schema;
  options: object;
  item: object;
  itemIndex: number;
  acceptFunction: Function;
  acceptText: string;
}>({
  errorMessages: ref([]),
  show: false,
  title: '',
  model: {},
  schema: {} as Schema,
  options: schema.options,
  item: {},
  itemIndex: 0,
  acceptFunction: () => {},
  acceptText: t('save'),
});

const snackbar = reactive({
  modelValue: false,
  text: t('valueCopied'),
  color: 'success',
  variant: 'tonal' as 'tonal',
  timeout: 1000,
});

function getErrorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message || error.message;
    return message || t('actionError');
  }
  if (error instanceof Error) {
    return error.message || t('actionError');
  }
  return t('actionError');
}

function showError(message?: string) {
  const text = message || t('actionError');
  // @ts-ignore
  if (toast != null && typeof toast.error === 'function') {
    // @ts-ignore
    toast.error(text);
    return;
  }
  snackbar.text = text;
  snackbar.color = 'error';
  snackbar.variant = 'tonal';
  snackbar.timeout = 3000;
  snackbar.modelValue = true;
}

function showSuccess(message: string) {
  snackbar.text = message;
  snackbar.color = 'success';
  snackbar.variant = 'tonal';
  snackbar.timeout = 1000;
  snackbar.modelValue = true;
}

async function saveDialogForm(isActive: Ref<boolean>) {
  const { valid, messages } = await popupReference.value.validate('messages');
  popup.errorMessages = messages;
  if (valid) {
    popup.acceptFunction();
    isActive.value = false;
  }
}

async function runClickLogic() {
  try {
    await onClick(schema, model);
    await runBtnLogic();
  } catch (error) {
    loading.value = false;
    showError(getErrorMessage(error));
  }
}

async function runBtnLogic() {
  try {
    switch (schema.mode) {
      case 'action':
        loading.value = true;
        const body = await createBodyObject();
        let payloadObject: any = {
          code: schema.config.code,
          body: body,
          params: {
            ...schema.config.params,
          },
          refreshModelOnCallback: true,
          callback: () => {
            loading.value = false;
          },
        };

        if (shouldWaitForSaveState && !formCurrentSaveState.value) {
          if (schema.config.emit) {
            const existingCallback = payloadObject.callback;
            payloadObject.callback = () => {
              existingCallback();
              actionHandlerEventBus.emit('form-action', schema.config.emit);
            };
          }
          caller.value = () => actionHandlerEventBus.emit('form-action', payloadObject);
          return;
        }

        if (schema.config.emit) {
          const existingCallback = payloadObject.callback;
          payloadObject.callback = () => {
            existingCallback();
            actionHandlerEventBus.emit('form-action', schema.config.emit);
          };
        }

        actionHandlerEventBus.emit('form-action', payloadObject);

        break;
      case 'form-and-action':
        popup.errorMessages = [];
        popup.title = schema.config.title;
        if (schema.config.modelReference) {
          popup.model = get(model, schema.config.modelReference, {});
        }
        if (schema.config.acceptText) {
          popup.acceptText = schema.config.acceptText;
        }
        popup.schema = schema.schema as any;
        popup.acceptFunction = async () => {
          let payloadObject = {
            code: schema.config.code,
            body: popup.model,
            params: {
              ...schema.config.params,
            },
            refreshModelOnCallback: true,
            callback: () => {
              loading.value = false;
            },
          };
          console.debug(
            `Popup model is ready to save, event [form-action] was emitted`,
            payloadObject,
          );
          loading.value = true;
          actionHandlerEventBus.emit('form-action', payloadObject);
        };
        popup.show = true;
        break;
      case 'copy':
        const value = get(model, schema.config.modelReference, '');
        await navigator.clipboard.writeText(value);
        showSuccess(t('valueCopied'));
        break;
      case 'api-call':
        loading.value = true;
        if (shouldWaitForSaveState && !formCurrentSaveState.value) {
          caller.value = apiCallMode;
          return;
        }
        await apiCallMode();

        break;
      case 'redirect': {
        const resolveRedirectValue = async (value: unknown): Promise<unknown> => {
          if (typeof value === 'string' && new RegExp(variableRegexp).test(value)) {
            const { resolvedText, allVariablesResolved } = await resolve(schema, value);
            return allVariablesResolved ? resolvedText : null;
          }

          if (Array.isArray(value)) {
            return Promise.all(value.map((item) => resolveRedirectValue(item)));
          }

          if (value && typeof value === 'object') {
            const nestedEntries = Object.entries(value as Record<string, unknown>);
            const resolvedNestedEntries = await Promise.all(
              nestedEntries.map(async ([key, nestedValue]) => {
                const resolvedNestedValue = await resolveRedirectValue(nestedValue);
                return [key, resolvedNestedValue] as const;
              }),
            );

            return Object.fromEntries(resolvedNestedEntries);
          }

          return value;
        };

        const paramsObject = await resolveRedirectValue(schema.config.params ?? {});
        const payloadObject = {
          mode: schema.mode,
          params: paramsObject,
          target: schema.config.target,
        };
        actionHandlerEventBus.emit('form-action', payloadObject);
        break;
      }
    }
  } catch (error) {
    loading.value = false;
    showError(getErrorMessage(error));
  }
}

async function apiCallMode() {
  const { resolvedText, allVariablesResolved } = await resolve(schema, schema.config.source, true);
  const body = await createBodyObject();
  if (allVariablesResolved) {
    try {
      const response = await axios({
        method: schema.config.method || 'POST',
        url: resolvedText,
        data: body,
      });

      if (schema.config.emit) {
        actionHandlerEventBus.emit('form-action', schema.config.emit);
      }

      // TODO - dalsza implementacja - co ma się dziać z response, jakie warianty
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      loading.value = false;
    }
  } else {
    loading.value = false;
  }
}

async function createBodyObject() {
  let body: Record<string, any> = {};
  if (schema.config.body == undefined) return body;
  const entries = Object.entries(schema.config.body);
  const resolvedEntries = await Promise.all(
    entries.map(async ([key, value]) => {
      if (typeof value === 'string' && variableRegexp.test(value)) {
        const { resolvedText, allVariablesResolved } = await resolve(schema, value as string);
        return [key, allVariablesResolved ? resolvedText : null];
      } else {
        return [key, value];
      }
    }),
  );

  resolvedEntries.forEach(([key, value]) => {
    body[key as string] = value;
  });

  return body;
}

onMounted(async () => {
  await bindLabel(schema);
  await bindProps(schema);
});
</script>

<style lang="css" scoped></style>
